import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { PacientesNoVolvieronResumen } from '@/application/metrics';
import { getPacientesNoVolvieronResumen } from '@/infrastructure/http/metricsApi';
import { useAuth } from '@/presentation/contexts/useAuth';
import { MetricBlock } from './MetricBlock';
import { MetricKpiCard } from './MetricKpiCard';
import { formatInteger } from './metricFormatters';

interface NoVolvieronBlockProps {
  tenantId: string;
  year: number;
  month: number;
}

/**
 * "Pacientes que no volvieron" de la clínica seleccionada: cuántos dejaron de venir
 * (ventana de recencia por defecto) y cuántos con el plan agotado. Se auto-carga.
 */
export function NoVolvieronBlock({ tenantId, year, month }: NoVolvieronBlockProps) {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<PacientesNoVolvieronResumen | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!token || !tenantId || !year || !month) {
      setData(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    getPacientesNoVolvieronResumen(token, { tenantId, year, month })
      .then((result) => {
        if (!cancelled) {
          setData(result);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setData(null);
          setError(err instanceof Error ? err.message : t('metrics.states.error.message'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, tenantId, year, month, t]);

  const openDetail = (planAgotado: boolean) => () => {
    const params = new URLSearchParams({ tenant: tenantId, year: String(year), month: String(month) });
    if (data) {
      params.set('desde', String(data.desde));
      params.set('hasta', String(data.hasta));
    }
    if (planAgotado) {
      params.set('planAgotado', '1');
    }
    navigate(`/reports/no-return?${params.toString()}`);
  };

  return (
    <MetricBlock
      title={t('metrics.noVolvieron.title')}
      description={data ? t('metrics.noVolvieron.description', { desde: data.desde, hasta: data.hasta }) : t('metrics.noVolvieron.descriptionShort')}
      isLoading={isLoading}
      error={error}
      isEmpty={!data}
      emptyMessage={t('metrics.states.empty.message')}
    >
      {data && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <MetricKpiCard
            label={t('metrics.noVolvieron.total')}
            value={formatInteger(data.total, i18n.language)}
            tone="amber"
            onDetail={openDetail(false)}
          />
          <MetricKpiCard
            label={t('metrics.noVolvieron.exhausted')}
            value={formatInteger(data.conPlanAgotado, i18n.language)}
            tone="red"
            onDetail={openDetail(true)}
          />
        </div>
      )}
    </MetricBlock>
  );
}
